type FieldType =
  | "text"
  | "number"
  | "email"
  | "date"
  | "phoneNumber"
  | "select";

type Field = {
  type: FieldType;
  name: string;
  label: string;
  hint: string;
  value: string;
  readOnly: boolean;
  validation: {
    required: boolean;
    minLength: number | null;
    maxLength: number | null;
  };
  dependsOn?: string;
  optionsList?: Array<{ label: string; value: string }>;
  optionsMap?: Record<string, Array<{ label: string; value: string }>>;
};

const fieldTypeMap: Partial<Record<FormField["type"], FieldType>> = {
  CharField: "text",
  IntegerField: "number",
  EmailField: "email",
  DateField: "date",
  PhoneNumberField: "phoneNumber",
  ChoiceField: "select",
};

export const formatFields = ({
  fields,
  providerName,
  providerExtraData,
  providerApiName,
}: {
  fields: FormField[];
  providerName: string;
  providerApiName?: string;
  providerExtraData: Record<string, unknown>;
}) => {
  // Custom mapping
  if (providerApiName === "paymob_cash_collection" && providerExtraData) {
    return fields.map((field, index) => {
      if (field.field_name === "state") {
        return {
          ...formatFormField(field),
          optionsList: Object.keys(providerExtraData.state_city).map(
            (state) => ({
              label: state,
              value: state,
            })
          ),
        };
      }

      if (field.field_name === "city") {
        const states = Object.keys(providerExtraData.state_city);
        const formattedCities = Object.values(providerExtraData.state_city).map(
          (stateCitiesArray: Array<string>) =>
            stateCitiesArray.map((city) => ({
              label: city,
              value: city,
            }))
        );

        const citiesEntries = states.map((state, index) => [
          state,
          formattedCities[index],
        ]);

        return {
          ...formatFormField(field),
          type: "select",
          dependsOn: fields[index - 1].field_name,
          optionsList: null,
          optionsMap: Object.fromEntries(citiesEntries),
        };
      }

      return formatFormField(field);
    });
  }

  if (providerName === "Fawry" && providerExtraData) {
    return fields.map((field, index) => {
      if (field.field_name === "state") {
        return {
          ...formatFormField(field),
          optionsList: Object.keys(providerExtraData.state_city_area).map(
            (state) => ({
              label: state,
              value: state,
            })
          ),
        };
      }

      if (field.field_name === "city") {
        return {
          ...formatFormField(field),
          type: "select",
          dependsOn: fields[index - 1].field_name,
          optionsList: null,
          optionsMap: Object.fromEntries(
            Object.entries(providerExtraData.state_city_area).map(
              ([state, { cities }]) => [
                state,
                Object.keys(cities).map((city) => ({
                  label: city,
                  value: city,
                })),
              ]
            )
          ),
        };
      }

      if (field.field_name === "area") {
        return {
          ...formatFormField(field),
          type: "select",
          dependsOn: fields[index - 1].field_name,
          optionsList: null,
          optionsMap: Object.fromEntries(
            Object.values(providerExtraData.state_city_area).flatMap(
              ({ cities }) =>
                Object.entries<{ areas: Record<string, unknown> }>(cities).map(
                  ([city, { areas }]) => [
                    city,
                    Object.keys(areas).map((area) => ({
                      label: area,
                      value: area,
                    })),
                  ]
                )
            )
          ),
        };
      }

      return formatFormField(field);
    });
  }

  if (providerName === "Cyber Source" && providerExtraData) {
    return fields.map((field, index) => {
      if (field.field_name === "country") {
        return {
          ...formatFormField(field),
          optionsList: Object.keys(providerExtraData.country_state).map(
            (country) => ({
              label: country,
              value: country,
            })
          ),
          value:
            field.value && providerExtraData.country_codes[field.value]
              ? providerExtraData.country_codes[field.value]
              : field.value,
        };
      }

      if (field.field_name === "state") {
        return {
          ...formatFormField(field),
          type: "select",
          dependsOn: fields[index - 2].field_name,
          optionsList: null,
          optionsMap: Object.fromEntries(
            Object.entries(providerExtraData.country_state).map(
              ([country, states]) => [
                country,
                states.map((state) => ({
                  label: state,
                  value: state,
                })),
              ]
            )
          ),
        };
      }
      return formatFormField(field);
    });
  }

  return fields.map(formatFormField);
};

export const formatFormField = (field: FormField) => {
  const fieldSchema: Field = {
    type: fieldTypeMap[field.type],
    name: field.field_name,
    label: field.label,
    hint: field.help_text,
    value: field.value,
    readOnly: field.read_only,
    validation: {
      required: field.required,
      minLength: field.min_length,
      maxLength: field.max_length,
    },
  };

  if (field.type === "ChoiceField") {
    fieldSchema.optionsList = Object.keys(field.choices ?? {}).map((key) => ({
      label: field.choices[key],
      value: key,
    }));
  }

  return fieldSchema;
};

export const formatPlans = ({
  plans,
  currency,
  providerRelatedFields,
}: {
  plans?: PaymentActionData["plans"];
  currency: string;
  providerRelatedFields: PaymentActionData["provider_related_fields"];
}) => {
  return plans
    ? plans.map((plan) => ({
        id: plan.installment_id,
        installmentPeriod: plan.installment_period,
        interestRate: plan.rate ?? null,
        amount: {
          value: `${plan.amount_per_month} ${currency}`,
          formatted: parseFloat(plan.amount_per_month),
          currency,
        },
        upfrontFees: plan.upfront_fees ?? null,
        issuerCode: providerRelatedFields?.issuer_code ?? null,
        issuerNameEn: providerRelatedFields?.issuer_name_en ?? null,
        issuerNameAr: providerRelatedFields?.issuer_name_ar ?? null,
        issuerLogoEn: providerRelatedFields?.issuer_logo_en ?? null,
        issuerLogoAr: providerRelatedFields?.issuer_logo_ar ?? null,
        processingFeesType: plan?.processing_fees_type ?? null,
        processingFeesAmount: plan?.processing_fees_amount ?? null,
        feeDisplayValue: plan?.fee_display_value ?? null,
      }))
    : [];
};
